import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { typesApi } from "../api/endpoints";
import { ApiError } from "../api/client";
import { SWATCHES, type Swatch } from "../lib/swatches";
import type { CardType } from "../types/domain";
import { IcoCheck, IcoChevDown, IcoClose, IcoPlus, IcoTrash } from "./icons";
import { ModalBackdrop } from "./ModalBackdrop";
import { useCanWrite } from "../hooks/useCanWrite";

interface Props {
  onClose: () => void;
}

export function TypesModal({ onClose }: Props) {
  const qc = useQueryClient();
  const canWrite = useCanWrite();
  const readOnly = !canWrite;
  const typesQuery = useQuery({
    queryKey: ["types"],
    queryFn: async () => (await typesApi.list()).types,
  });
  const types = typesQuery.data ?? [];

  const invalidate = () => qc.invalidateQueries({ queryKey: ["types"] });

  const createMutation = useMutation({
    mutationFn: () => {
      const sw = SWATCHES[types.length % SWATCHES.length]!;
      return typesApi.create({ name: "Новый тип", ...sw });
    },
    onSuccess: invalidate,
  });

  const patchMutation = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Partial<Omit<CardType, "id" | "isPreset">> }) =>
      typesApi.patch(id, patch),
    onMutate: async ({ id, patch }) => {
      await qc.cancelQueries({ queryKey: ["types"] });
      const prev = qc.getQueryData<CardType[]>(["types"]);
      if (prev) {
        qc.setQueryData<CardType[]>(
          ["types"],
          prev.map((t) => (t.id === id ? { ...t, ...patch } : t)),
        );
      }
      return { prev };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) qc.setQueryData(["types"], ctx.prev);
    },
    onSettled: invalidate,
  });

  const reorderMutation = useMutation({
    mutationFn: ({ ids, expectedOrder }: { ids: string[]; expectedOrder: string[] }) =>
      typesApi.reorder(ids, { expectedOrder }),
    onMutate: async ({ ids }) => {
      await qc.cancelQueries({ queryKey: ["types"] });
      const prev = qc.getQueryData<CardType[]>(["types"]);
      if (prev) {
        const byId = new Map(prev.map((t) => [t.id, t]));
        const reordered = ids.map((id) => byId.get(id)).filter((t): t is CardType => !!t);
        qc.setQueryData<CardType[]>(["types"], reordered);
      }
      return { prev };
    },
    onError: (err, _vars, ctx) => {
      if (ctx?.prev) qc.setQueryData(["types"], ctx.prev);
      if (err instanceof ApiError && err.status === 409) {
        window.alert(
          "Порядок типов был изменён другим пользователем. Обновим и попробуйте ещё раз.",
        );
      }
    },
    onSettled: invalidate,
  });

  const move = (id: string, dir: -1 | 1) => {
    const idx = types.findIndex((t) => t.id === id);
    if (idx < 0) return;
    const next = idx + dir;
    if (next < 0 || next >= types.length) return;
    // Baseline для optimistic-lock: порядок, который клиент видит СЕЙЧАС.
    const baseline = types.map((t) => t.id);
    const ids = [...baseline];
    [ids[idx], ids[next]] = [ids[next]!, ids[idx]!];
    reorderMutation.mutate({ ids, expectedOrder: baseline });
  };

  const removeMutation = useMutation({
    mutationFn: (id: string) => typesApi.remove(id),
    onSuccess: invalidate,
    onError: (err) => {
      if (err instanceof ApiError) {
        if (err.code === "type_in_use") {
          const d = err.details as { cards?: number; templates?: number } | undefined;
          window.alert(
            `Тип используется в ${d?.cards ?? 0} карточках и ${d?.templates ?? 0} шаблонах. Сначала переназначьте.`,
          );
        } else if (err.code === "preset_immutable") {
          window.alert("Системные пресет-типы удалить нельзя.");
        } else {
          window.alert(err.message);
        }
      }
    },
  });

  return (
    <ModalBackdrop onClose={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <h2>Типы карточек</h2>
          <div className="spacer" />
          <button type="button" className="modal-close" onClick={onClose} aria-label="Закрыть">
            <IcoClose size={16} />
          </button>
        </div>
        <div className="modal-body">
          <p style={{ fontSize: 12.5, color: "var(--text-3)", margin: "0 0 14px" }}>
            Задайте типы карточек, которые используются в ваших запусках. Цвет отражается на всей доске.
          </p>
          <div className="types-list">
            {types.map((t, i) => (
              <TypeRow
                key={t.id}
                type={t}
                readOnly={readOnly}
                isFirst={i === 0}
                isLast={i === types.length - 1}
                onRename={(name) => patchMutation.mutate({ id: t.id, patch: { name } })}
                onSwatch={(sw) => patchMutation.mutate({ id: t.id, patch: sw })}
                onToggleLanding={() =>
                  patchMutation.mutate({
                    id: t.id,
                    patch: { kind: t.kind === "landing" ? "generic" : "landing" },
                  })
                }
                onMoveUp={() => move(t.id, -1)}
                onMoveDown={() => move(t.id, 1)}
                onDelete={() => {
                  if (window.confirm(`Удалить тип «${t.name}»?`)) {
                    removeMutation.mutate(t.id);
                  }
                }}
              />
            ))}
          </div>
          {!readOnly && (
            <button
              type="button"
              className="add-type-row"
              onClick={() => createMutation.mutate()}
              disabled={createMutation.isPending}
            >
              <IcoPlus size={13} /> Добавить тип
            </button>
          )}
        </div>
        <div className="modal-foot">
          <div className="spacer" />
          <button type="button" className="btn-primary" onClick={onClose}>
            <IcoCheck size={14} /> Готово
          </button>
        </div>
      </div>
    </ModalBackdrop>
  );
}

interface TypeRowProps {
  type: CardType;
  readOnly?: boolean;
  isFirst?: boolean;
  isLast?: boolean;
  onRename: (name: string) => void;
  onSwatch: (sw: Swatch) => void;
  onToggleLanding: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onDelete: () => void;
}

function TypeRow({
  type,
  readOnly = false,
  isFirst = false,
  isLast = false,
  onRename,
  onSwatch,
  onToggleLanding,
  onMoveUp,
  onMoveDown,
  onDelete,
}: TypeRowProps) {
  const [name, setName] = useState(type.name);
  const [pickerOpen, setPickerOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    setName(type.name);
  }, [type.name]);

  useEffect(() => {
    if (!pickerOpen) return;
    const close = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setPickerOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [pickerOpen]);

  const commitName = () => {
    const trimmed = name.trim();
    if (!trimmed || trimmed === type.name) {
      setName(type.name);
      return;
    }
    onRename(trimmed);
  };

  return (
    <div className="type-row">
      <div
        ref={ref}
        className="swatch"
        style={{ background: type.color, cursor: readOnly ? "default" : "pointer" }}
        onClick={() => !readOnly && setPickerOpen((v) => !v)}
        title={readOnly ? undefined : "Выбрать цвет"}
      >
        {pickerOpen && !readOnly && (
          <div className="swatch-picker" onClick={(e) => e.stopPropagation()}>
            {SWATCHES.map((s, i) => (
              <div
                key={i}
                style={{ background: s.color }}
                onClick={() => {
                  onSwatch(s);
                  setPickerOpen(false);
                }}
              />
            ))}
          </div>
        )}
      </div>
      <input
        value={name}
        onChange={(e) => setName(e.target.value)}
        onBlur={commitName}
        disabled={readOnly}
        onKeyDown={(e) => {
          if (e.key === "Enter") (e.target as HTMLInputElement).blur();
          if (e.key === "Escape") {
            setName(type.name);
            (e.target as HTMLInputElement).blur();
          }
        }}
      />
      <span style={{ fontSize: 11, color: "var(--text-3)", fontFamily: "JetBrains Mono, monospace" }}>
        {type.color}
      </span>
      <button
        type="button"
        className={"type-kind-toggle" + (type.kind === "landing" ? " is-on" : "")}
        onClick={onToggleLanding}
        disabled={type.isPreset || readOnly}
        title={
          type.isPreset
            ? "Поведение пресет-типа менять нельзя"
            : type.kind === "landing"
              ? "Сейчас тип-лендинг: карточка ссылается на объект Лендинг. Нажмите, чтобы сделать обычным."
              : "Сделать тип-лендингом: в карточках появится выбор лендинга вместо текста."
        }
      >
        Лендинг
      </button>
      {!readOnly && (
        <div className="type-row-reorder">
          <button
            type="button"
            className="row-reorder"
            onClick={onMoveUp}
            disabled={isFirst}
            title={isFirst ? "Первый — вверх некуда" : "Переместить выше"}
            aria-label="Переместить выше"
          >
            <IcoChevDown size={13} style={{ transform: "rotate(180deg)" }} />
          </button>
          <button
            type="button"
            className="row-reorder"
            onClick={onMoveDown}
            disabled={isLast}
            title={isLast ? "Последний — вниз некуда" : "Переместить ниже"}
            aria-label="Переместить ниже"
          >
            <IcoChevDown size={13} />
          </button>
        </div>
      )}
      {!readOnly && (
        <button
          type="button"
          className="row-del"
          onClick={onDelete}
          disabled={type.isPreset}
          title={type.isPreset ? "Системный тип нельзя удалить" : "Удалить"}
          style={type.isPreset ? { opacity: 0.35, cursor: "not-allowed" } : undefined}
        >
          <IcoTrash size={13} />
        </button>
      )}
    </div>
  );
}
